import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import {
  formatSiteAddressLine,
  showsFallbackAddress,
} from "../../lib/format-site-address-line"

// Connects to data-controller="ui--multi-select"
// Multi-select dropdown — a 1:1 behavioural replica of the @a-safe-digital React
// MultiSelect (default variant). A trigger summarizing the selection as comma-joined
// labels opens a panel with a search input, an optional "Select All" row and a checkbox
// option list. Selection is backed by native checkboxes (name[]), so the form submits
// with zero JS.
//
// Two modes, like the React component:
//   * data-driven (default): options live in the DOM; search filters them client-side.
//   * async (when `search-url` is set): search fetches matches from the endpoint
//     (debounced, with a loading spinner) — React's async `filterOptions`. No pagination.
export default class extends Controller {
  static targets = [
    "container",
    "trigger",
    "summary",
    "panel",
    "search",
    "clearSearch",
    "clearAll",
    "option",
    "checkbox",
    "selectAll",
    "selectAllLabel",
    "empty",
    "searchIcon",
    "spinner",
    "optionsContainer",
    "checkTemplate",
    "values",
    "primaryValue",
    "primaryTemplate",
  ]

  static values = {
    searchable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    max: { type: Number, default: 0 },
    min: { type: Number, default: 0 },
    placeholder: { type: String, default: "Select" },
    allSelectedLabel: { type: String, default: "All items are selected." },
    searchUrl: { type: String, default: "" },
    displayAttribute: { type: String, default: "name" },
    fieldName: { type: String, default: "" },
    selectedFirst: { type: Boolean, default: false },
    showEntityDetails: { type: Boolean, default: false },
    noAddressLabel: { type: String, default: "" },
    fallbackAddressTooltip: { type: String, default: "" },
  }

  connect() {
    this._isOpen = false
    this._async = this.searchUrlValue.length > 0
    this._initialLoaded = false
    this._debounceTimer = null
    this._nextPosition = 0

    this._outsideClickHandler = this._handleOutsideClick.bind(this)
    this._keydownHandler = this._handleKeydown.bind(this)
    document.addEventListener("click", this._outsideClickHandler)
    document.addEventListener("keydown", this._keydownHandler)

    this._syncSelectAll()
    this._enforceMax()
    this._enforceMin()
    // Reconcile on connect so a lone selection carries its marker from the first
    // paint. A server-rendered selection with no primary and several values stays
    // blank: it is a genuine choice, and the form submits it blank rather than
    // inventing an answer.
    this._reconcilePrimary()
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    document.removeEventListener("click", this._outsideClickHandler)
    document.removeEventListener("keydown", this._keydownHandler)
  }

  // The search box sits inside the host's form but is only a filter: its native
  // `change` on blur would reach a `change->ui--auto-submit#submit` form and submit
  // it before the row being clicked is selected, and Enter would submit the form
  // implicitly (CONEK-8137).
  searchTargetConnected(input) {
    input.addEventListener("change", this._stopPropagation)
    input.addEventListener("keydown", this._preventEnter)
  }

  searchTargetDisconnected(input) {
    input.removeEventListener("change", this._stopPropagation)
    input.removeEventListener("keydown", this._preventEnter)
  }

  _stopPropagation(event) {
    event.stopPropagation()
  }

  _preventEnter(event) {
    if (event.key === "Enter") event.preventDefault()
  }

  // --- Open / close ---

  toggle(event) {
    event?.preventDefault()
    if (this.disabledValue) return
    if (this._isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.disabledValue || this._isOpen) return
    this._floatSelected()
    this.panelTarget.classList.remove("hidden")
    this.containerTarget.dataset.state = "open"
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this._isOpen = true
    if (this.searchableValue && this.hasSearchTarget) this.searchTarget.focus()
    // Async: lazy-load the first page the first time the panel opens.
    if (this._async && !this._initialLoaded) {
      this._initialLoaded = true
      this._performSearch("")
    }
  }

  close() {
    if (!this._isOpen) return
    this.panelTarget.classList.add("hidden")
    this.containerTarget.dataset.state = "closed"
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this._isOpen = false
  }

  // --- Search ---

  filter() {
    if (!this.hasSearchTarget) return
    const query = this.searchTarget.value.trim()

    if (this.hasClearSearchTarget) {
      this.clearSearchTarget.classList.toggle("hidden", query.length === 0)
    }

    if (this._async) {
      if (this._debounceTimer) clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(() => this._performSearch(query), 300)
      return
    }

    this._floatSelected()

    // Data-driven: client-side substring filter on the label.
    let visible = 0
    const lower = query.toLowerCase()
    this.optionTargets.forEach((li) => {
      const match = (li.dataset.label || "").toLowerCase().includes(lower)
      li.style.display = match ? "" : "none"
      if (match) visible += 1
    })
    if (this.hasEmptyTarget)
      this.emptyTarget.classList.toggle("hidden", visible > 0)
    if (this.hasSelectAllLabelTarget) {
      this.selectAllLabelTarget.textContent = query.length
        ? "Select All (Filtered)"
        : "Select All"
    }
    this._syncSelectAll()
    this._scrollOptionsToTop()
  }

  clearSearch() {
    if (!this.hasSearchTarget) return
    this.searchTarget.value = ""
    this.filter()
    this.searchTarget.focus()
  }

  // --- Async fetching (mirrors React's async filterOptions; no pagination) ---

  async _performSearch(query) {
    if (this.disabledValue || !this._async) return
    this._setLoading(true)
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      // Search endpoints are inconsistent about the query param name: the
      // PackSupport::SearchParams ones (users/contacts/tasks) read `query`,
      // others (user_tiers, graph_subscriptions, quick search) read `q`. Send
      // BOTH so async search filters everywhere — sending `q` alone left the
      // query nil server-side, so these pickers returned unfiltered,
      // alphabetical results (CONEK-4230).
      url.searchParams.set("q", query)
      url.searchParams.set("query", query)
      const response = await get(url.toString(), {
        credentials: "include",
        responseKind: "json",
      })
      let items = []
      if (response.ok) {
        const data = await response.json
        const raw = Array.isArray(data)
          ? data
          : data.data || data.items || data.results || data.options || []
        items = raw.map((it) => {
          const item = {
            value: String(it.id ?? it.value),
            label: String(
              it[this.displayAttributeValue] ??
                it.name ??
                it.label ??
                it.id ??
                it.value,
            ),
          }
          if (this.showEntityDetailsValue) {
            item.tags = it.customerCode ? [String(it.customerCode)] : []
            item.sublabel = formatSiteAddressLine(it.address)
            if (showsFallbackAddress(it.address))
              item.sublabelTitle = this.fallbackAddressTooltipValue
          }
          return item
        })
      }
      this._renderResults(items, query)
    } catch {
      this._renderResults([], query)
    } finally {
      this._setLoading(false)
    }
  }

  // Keep the selected (checked) options in the DOM so the trigger summary and the
  // selection order survive a search, and append the freshly fetched ones (deduped
  // by value). A selected option that does not match the query is HIDDEN, not
  // removed: the panel then shows what was searched for instead of burying it under
  // every current selection (CONEK-6625 — a 60-member group pushed the match to row
  // 61 of 61). This matches both the React component, whose panel renders exactly
  // `filteredOptions` (selection lives in `value`, not in the list), and this
  // controller's own data-driven branch, which hides non-matching rows the same way.
  // Visibility never affects what is submitted — that is the hidden `values` inputs.
  _renderResults(items, query = "") {
    this.optionTargets.forEach((li) => {
      const checkbox = li.querySelector("input[type=checkbox]")
      if (checkbox && !checkbox.checked) li.remove()
    })
    const present = new Set(this.checkboxTargets.map((c) => c.value))
    items.forEach((item) => {
      if (present.has(item.value)) return
      this.optionsContainerTarget.appendChild(this._buildOption(item))
      present.add(item.value)
    })
    this._floatSelected()

    const lower = query.trim().toLowerCase()
    const matched = new Set(items.map((item) => item.value))
    let visible = 0
    this.optionTargets.forEach((li) => {
      const value = li.querySelector("input[type=checkbox]")?.value
      const shows =
        lower.length === 0 ||
        matched.has(value) ||
        (li.dataset.label || "").toLowerCase().includes(lower)
      li.style.display = shows ? "" : "none"
      if (shows) visible += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visible > 0)
    }
    this._updateSummary()
    this._enforceMin()
    this._scrollOptionsToTop()
  }

  // Only between interactions: moving rows while picking shifts them under the pointer.
  _floatSelected() {
    if (!this.selectedFirstValue) return
    this.optionTargets.forEach((li) => {
      li.dataset.position ??= String(this._nextPosition++)
    })
    const selected = this.hasValuesTarget
      ? [...this.valuesTarget.querySelectorAll("input")].map((i) => i.value)
      : []
    const rank = (li) => {
      const value = li.querySelector("input[type=checkbox]")?.value
      const index = selected.indexOf(value)
      return index === -1 ? selected.length : index
    }
    this.optionTargets
      .sort((a, b) => rank(a) - rank(b) || a.dataset.position - b.dataset.position)
      .forEach((li) => this.optionsContainerTarget.appendChild(li))
  }

  // The option list is a different list after a search, so showing it from
  // wherever the previous one was scrolled to is meaningless.
  _scrollOptionsToTop() {
    if (this.hasOptionsContainerTarget)
      this.optionsContainerTarget.parentElement.scrollTop = 0
  }

  // The hidden inputs carry the selection in the order it was made — appending on
  // select and removing on deselect. Submitting the option checkboxes instead would
  // hand the server the list order, silently re-sorting the operator's choice.
  _syncValue(checkbox) {
    if (!this.hasValuesTarget) return
    const existing = this._valueInput(checkbox.value)
    if (checkbox.checked && !existing) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.fieldNameValue
      input.value = checkbox.value
      input.dataset.value = checkbox.value
      this.valuesTarget.appendChild(input)
    } else if (!checkbox.checked && existing) {
      existing.remove()
    }
  }

  _valueInput(value) {
    return this.hasValuesTarget
      ? [...this.valuesTarget.querySelectorAll("input")].find(
          (input) => input.value === value,
        )
      : null
  }

  // Selected checkboxes in selection order (the order of the hidden inputs).
  _selectedInOrder() {
    if (!this.hasValuesTarget) return this._selectedCheckboxes()
    return [...this.valuesTarget.querySelectorAll("input")]
      .map((input) => this.checkboxTargets.find((c) => c.value === input.value))
      .filter(Boolean)
  }

  _buildOption(item) {
    const li = document.createElement("li")
    li.className = "m-0 list-none"
    li.setAttribute("data-ui--multi-select-target", "option")
    li.dataset.label = item.label

    const row = document.createElement("div")
    row.className =
      "select-item relative flex items-center gap-2 cursor-pointer p-2.5 text-foreground-medium hover:text-foreground/80 hover:bg-input border-b border-border"
    row.setAttribute("role", "option")
    row.setAttribute("aria-selected", "false")
    row.setAttribute("data-action", "click->ui--multi-select#toggleOption")

    const input = document.createElement("input")
    input.type = "checkbox"
    input.value = item.value
    input.className = "sr-only"
    input.setAttribute("data-ui--multi-select-target", "checkbox")
    input.setAttribute("data-action", "change->ui--multi-select#optionChanged")

    const box = document.createElement("span")
    box.className =
      "flex items-center justify-center size-4 shrink-0 rounded border border-input"
    box.setAttribute("data-multi-select-box", "")
    if (this.hasCheckTemplateTarget) {
      const check =
        this.checkTemplateTarget.content.firstElementChild.cloneNode(true)
      check.classList.add("hidden!")
      box.appendChild(check)
    }

    row.append(input, box, this._buildLabel(item))
    if (this.hasPrimaryTemplateTarget) {
      row.appendChild(
        this.primaryTemplateTarget.content.firstElementChild.cloneNode(true),
      )
    }
    li.appendChild(row)
    return li
  }

  _buildLabel(item) {
    const tags = (item.tags || []).filter(Boolean)
    const sublabel =
      item.sublabel ||
      (this.showEntityDetailsValue ? this.noAddressLabelValue : "")

    if (!this.showEntityDetailsValue && !item.sublabel && tags.length === 0) {
      const text = document.createElement("span")
      text.textContent = item.label
      return text
    }

    const details = document.createElement("span")
    details.className = "flex min-w-0 flex-1 flex-col items-start overflow-hidden"
    details.setAttribute("data-multi-select-details", "")

    const top = document.createElement("span")
    top.className = "flex w-full items-center gap-2"
    const name = document.createElement("span")
    name.className = "flex-1 truncate"
    name.textContent = item.label
    top.appendChild(name)
    tags.forEach((tag) => {
      const chip = document.createElement("span")
      chip.className =
        "bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-xs font-medium"
      chip.setAttribute("data-multi-select-tag", "")
      chip.textContent = tag
      top.appendChild(chip)
    })
    details.appendChild(top)

    if (sublabel) {
      const line = document.createElement("span")
      line.className = "text-muted-foreground w-full truncate text-xs"
      line.setAttribute("data-multi-select-sublabel", "")
      line.textContent = sublabel
      if (item.sublabelTitle) line.title = item.sublabelTitle
      details.appendChild(line)
    }
    return details
  }

  // Public: select an item created outside the panel — the "+ Create" drawer — adding
  // its option first when the list does not carry it yet. Returns the checkbox, or
  // null when the item was unusable.
  //
  // Deliberately public. A host controller reaching into a private `_` method is
  // exactly the coupling this replaces: it breaks silently on any refactor here, and
  // the host can only find out at runtime.
  commitItem({ id, label, sublabel, sublabelTitle, tags } = {}) {
    const value = id == null ? "" : String(id)
    if (value.length === 0) return null

    let checkbox = this.checkboxTargets.find((c) => c.value === value)
    if (!checkbox) {
      const li = this._buildOption({
        value,
        label: label == null ? value : String(label),
        sublabel,
        sublabelTitle,
        tags,
      })
      this.optionsContainerTarget.appendChild(li)
      checkbox = li.querySelector("input[type=checkbox]")
    }
    if (!checkbox) return null

    if (!checkbox.checked) {
      checkbox.checked = true
      this._applySelection(checkbox)
    }
    return checkbox
  }

  // --- Selection ---

  // Rows are not <label>s (see the component template), so a click has to flip the
  // checkbox here. Ignore a click on the checkbox itself — it toggles natively and
  // reaches us through `change`.
  toggleOption(event) {
    const checkbox = event.currentTarget.querySelector("input[type=checkbox]")
    if (!checkbox || checkbox.disabled || event.target === checkbox) return
    const before = this._submittedValues()
    checkbox.checked = !checkbox.checked
    this._applySelection(checkbox)
    this._notifyChangeSince(before)
  }

  // Keyboard toggling of the sr-only checkbox.
  optionChanged(event) {
    const requested = event.target.checked
    this._applySelection(event.target)
    // The native `change` is already bubbling: keep a reverted toggle (min) from
    // reaching the form as if the selection had changed.
    if (event.target.checked !== requested) event.stopPropagation()
  }

  _applySelection(checkbox) {
    // Keep at least `min` selected: revert an uncheck that would drop below it.
    // Mouse clicks on a locked row are already blocked via pointer-events-none;
    // this guard covers keyboard toggling of the sr-only checkbox.
    if (
      !checkbox.checked &&
      this.minValue > 0 &&
      this._selectedCheckboxes().length < this.minValue
    ) {
      checkbox.checked = true
    }
    this._syncValue(checkbox)
    this._paintCheckbox(checkbox)
    this._updateSummary()
    this._syncSelectAll()
    this._enforceMax()
    this._enforceMin()
    this._reconcilePrimary()
  }

  // A checkbox flipped from code fires no `change`, so an enclosing form wired
  // `change->ui--auto-submit#submit` never heard about the pick (CONEK-8137).
  // The user's picks made from code (row click, Select All row, Clear all) end
  // here; the keyboard path rides the checkbox's native `change`. commitItem and
  // setPrimary do not notify yet.
  // Dispatched from the root, after the hidden values are in, so it bubbles to the
  // form without re-entering optionChanged — and only when the values moved, so a
  // min/max bounce does not submit.
  _notifyChangeSince(before) {
    if (this._submittedValues() === before) return
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _submittedValues() {
    const values = this.hasValuesTarget
      ? [...this.valuesTarget.querySelectorAll("input")].map((i) => i.value)
      : this._selectedCheckboxes().map((c) => c.value)
    return values.join("\n")
  }

  toggleSelectAllRow(event) {
    if (event.target === this.selectAllTarget) return
    const before = this._submittedValues()
    this.selectAllTarget.checked = !this.selectAllTarget.checked
    this._applySelectAll()
    this._notifyChangeSince(before)
  }

  toggleSelectAll(event) {
    const requested = event.target.checked
    this._applySelectAll()
    if (event.target.checked !== requested) event.stopPropagation()
  }

  _applySelectAll() {
    const checked = this.selectAllTarget.checked
    // A `min` makes "deselect all" invalid — keep the current selection and
    // bounce the select-all box back on. (_applySelection's revert can't help here
    // because this mutates checkboxes directly without `change` events.)
    if (!checked && this.minValue > 0) {
      this.selectAllTarget.checked = true
      return
    }
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.disabled) return
      const li = checkbox.closest("li")
      if (li && li.style.display === "none") return
      checkbox.checked = checked
      this._syncValue(checkbox)
      this._paintCheckbox(checkbox)
    })
    this._updateSummary()
    this._enforceMax()
    this._enforceMin()
    this._reconcilePrimary()
  }

  clearAll(event) {
    event?.preventDefault()
    const before = this._submittedValues()
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.checked = false
        this._syncValue(checkbox)
        this._paintCheckbox(checkbox)
      }
    })
    this._updateSummary()
    this._syncSelectAll()
    this._enforceMax()
    this._enforceMin()
    this._reconcilePrimary()
    this._notifyChangeSince(before)
  }

  // --- Visual + summary helpers ---

  _paintCheckbox(checkbox) {
    const row = checkbox.closest(".select-item")
    if (!row) return
    row.setAttribute("aria-selected", checkbox.checked ? "true" : "false")
    const box = row.querySelector("[data-multi-select-box]")
    if (box) {
      box.classList.toggle("bg-primary", checkbox.checked)
      box.classList.toggle("border-primary", checkbox.checked)
      box.classList.toggle("border-input", !checkbox.checked)
      const check = box.querySelector("svg")
      if (check) check.classList.toggle("hidden!", !checkbox.checked)
    }
  }

  // --- Primary marker ---

  // Mark the clicked row as primary. The row's own click handler toggles the option,
  // so this must not reach it.
  setPrimary(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasPrimaryValueTarget) return

    const checkbox = event.currentTarget
      .closest(".select-item")
      ?.querySelector("input[type=checkbox]")
    if (!checkbox || !checkbox.checked) return

    this.primaryValueTarget.value = checkbox.value
    this._paintPrimary()
  }

  // Keep the primary pointing at something selected, and NEVER choose between
  // alternatives on the user's behalf.
  //
  //   * the current primary is still selected -> keep it;
  //   * exactly one value is selected -> it is the primary, unambiguously;
  //   * otherwise (two or more selected and the primary gone) -> blank, and the
  //     user picks.
  //
  // That last branch is the whole point. Adopting the first selection there reads
  // as a convenience and is not one: it silently makes a choice the user did not
  // make, on a field whose whole purpose is to record which one they chose. The
  // consumer that needs a primary refuses a blank one and says so, which is the
  // honest outcome (CONEK-7560).
  _reconcilePrimary() {
    if (!this.hasPrimaryValueTarget) return

    const selected = this._selectedInOrder().map((c) => c.value)
    const current = this.primaryValueTarget.value
    if (!selected.includes(current)) {
      this.primaryValueTarget.value = selected.length === 1 ? selected[0] : ""
    }
    this._paintPrimary()
  }

  _paintPrimary() {
    if (!this.hasPrimaryValueTarget) return

    const current = this.primaryValueTarget.value
    this.checkboxTargets.forEach((checkbox) => {
      const star = checkbox
        .closest(".select-item")
        ?.querySelector("[data-multi-select-primary]")
      if (!star) return

      star.classList.toggle("invisible", !checkbox.checked)
      const marked = checkbox.checked && checkbox.value === current
      star.setAttribute("aria-pressed", marked ? "true" : "false")
      star
        .querySelector("[data-multi-select-primary-on]")
        ?.classList.toggle("hidden!", !marked)
      star
        .querySelector("[data-multi-select-primary-off]")
        ?.classList.toggle("hidden!", marked)
    })
  }

  _selectedCheckboxes() {
    return this.checkboxTargets.filter((c) => c.checked)
  }

  _updateSummary() {
    const selected = this._selectedCheckboxes()
    const total = this.checkboxTargets.length
    if (selected.length === 0) {
      this.summaryTarget.textContent = this.placeholderValue
      this.summaryTarget.classList.add("text-foreground-medium")
    } else if (!this._async && selected.length === total) {
      this.summaryTarget.textContent = this.allSelectedLabelValue
      this.summaryTarget.classList.remove("text-foreground-medium")
    } else {
      this.summaryTarget.textContent = this._selectedInOrder()
        .map((c) => c.closest("li").dataset.label)
        .join(", ")
      this.summaryTarget.classList.remove("text-foreground-medium")
    }
    this.triggerTarget.dataset.value = selected.length > 0 ? "true" : "false"
    if (this.hasClearAllTarget) {
      this.clearAllTarget.classList.toggle("hidden", selected.length === 0)
    }
  }

  _syncSelectAll() {
    if (!this.hasSelectAllTarget) return
    const visible = this.checkboxTargets.filter((c) => {
      const li = c.closest("li")
      return !c.disabled && (!li || li.style.display !== "none")
    })
    const allChecked = visible.length > 0 && visible.every((c) => c.checked)
    this.selectAllTarget.checked = allChecked
    this._paintCheckbox(this.selectAllTarget)
  }

  _enforceMax() {
    if (!this.maxValue || this.maxValue <= 0) return
    const reached = this._selectedCheckboxes().length >= this.maxValue
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.checked) checkbox.disabled = reached
      const row = checkbox.closest(".select-item")
      if (row) row.classList.toggle("opacity-50", !checkbox.checked && reached)
    })
  }

  // Lock the last `min` selected options so the selection can never drop below min.
  // We must NOT use `disabled` here: a disabled checkbox is excluded from form
  // submission, so locking the sole selected option would submit an empty selection.
  // Instead we block pointer interaction and mirror the component's disabled-option
  // styling, while the checkbox stays enabled so it still submits. Keyboard toggles
  // are reverted in optionChanged(). Only the checked boxes are touched, so this is
  // disjoint from _enforceMax (which locks the unchecked ones at max).
  _enforceMin() {
    if (!this.minValue || this.minValue <= 0) return
    const locked = this._selectedCheckboxes().length <= this.minValue
    this._selectedCheckboxes().forEach((checkbox) => {
      const row = checkbox.closest(".select-item")
      if (!row) return
      row.classList.toggle("opacity-50", locked)
      row.classList.toggle("pointer-events-none", locked)
      row.classList.toggle("cursor-not-allowed", locked)
    })
  }

  // --- Loading indicator (async) ---

  _setLoading(loading) {
    if (this.hasSearchIconTarget)
      this.searchIconTarget.classList.toggle("hidden", loading)
    if (this.hasSpinnerTarget)
      this.spinnerTarget.classList.toggle("hidden", !loading)
  }

  // --- Keyboard / outside click ---

  _handleKeydown(event) {
    if (this._isOpen && event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
    }
  }

  _handleOutsideClick(event) {
    if (this._isOpen && !this.element.contains(event.target)) this.close()
  }
}
